import type {
    CoverableChangesDTO,
    CoverableDTO,
    DiffDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { OPCRowInformation } from '../../../../config/flavourConfig';
import type {
    AdultAccidentDTO,
    ChildAccidentDTO,
    YoungAccidentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/accident';
import {
    ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
    ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
    CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
} from '../../../../constants/term';
import { getTermMonetaryValue, getTermValue } from '../../../../utils/termsUtil';
import ViewHippoInformationModal from '../../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';
import { mapContractTypeToLabel } from '../../../OpcEntryPage/components/EditCoverableAttribute/accident/AdultAccContractTypeChange';
import { formatArrayToString } from '../../../../utils/stringUtil';
import { getCoverableDiffsDTO, isChangeTypeAvailable } from '../../../../utils/opcChangesUtil';
import { AdjustItemKeys } from '../adjustDetails';

const PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE = 'ACCPermanentInjuryToothDamageCov';
const CHILD_PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE = 'ACCChildPermInjuryAccidentCov';
const LINE_PREFIX_ACC = 'ACC';
const EMPLOYMENT_FIELD_NAME = 'Employment';
const CONTRACT_TYPE_FIELD_NAME = 'ContractType';
const FULLTIME_OR_LEISURE_FIELD_NAME = 'FullTimeOrLeisure';

const getAdultAccidentAdjustDetailsConfig = (
    adult: AdultAccidentDTO,
    changes: DiffDTO[],
    isOpc: boolean
): OPCRowInformation[] => {
    const coverableId = adult.fixedId ?? '';
    const selectedBeneficiary = adult.beneficiaries ?? [];
    const beneficiary: OPCRowInformation[] =
        selectedBeneficiary && selectedBeneficiary?.length > 0
            ? [
                  {
                      key: AdjustItemKeys.ACC_ADULT_BENEFICIARY,
                      // Todo: Use hippo label when label id is define in document [Hippo ACC]
                      name: 'Begunstiget',
                      fieldValue: formatArrayToString(
                          selectedBeneficiary?.map(
                              (beneficiary) => beneficiary.fullName
                          ) as string[],
                          ',',
                          'og'
                      ),
                      isAdjustableItem: false,
                      labelType: 'normal',
                      hidden: isOpc
                  }
              ]
            : [];
    const isEmploymentAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'attribute',
            attributeFieldName: EMPLOYMENT_FIELD_NAME
        },
        changes
    );
    const isInsuranceSumAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'term',
            coverageId: PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
            termId: ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
        },
        changes
    );
    const isInjuryDegreeAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'term',
            coverageId: PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
            termId: ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM
        },
        changes
    );
    const isContractTypeAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'attribute',
            attributeFieldName: CONTRACT_TYPE_FIELD_NAME
        },
        changes
    );
    const isFullTimeOrLeisureAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'attribute',
            attributeFieldName: FULLTIME_OR_LEISURE_FIELD_NAME
        },
        changes
    );

    return [
        {
            key: AdjustItemKeys.ACC_ADULT_EMPLOYMENT,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            name: 'Beskæftigelse',
            fieldValue: adult.employment ?? '',
            isAdjustableItem: true,
            labelType: 'normal',
            opcInfo: {
                changeType: 'attribut',
                productName: 'top_acc_accident',
                coverableId,
                attribute: 'adultAccEmployment'
            },
            hidden: isOpc && !isEmploymentAvailableForUpdate
        },
        {
            key: AdjustItemKeys.ACC_ADULT_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            name: 'Forsikringssum',
            fieldValue: getTermMonetaryValue(
                adult,
                PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
                ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
            ),
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                // Todo: Use hippo label when label id is define in document [Hippo ACC]
                changeType: 'tilpas',
                productName: 'top_acc_accident',
                coverableId,
                coverageId: PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
                adjustAction: 'Limit',
                termId: ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
            },
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    // Todo: Use hippo label when label id is define in document [Hippo ACC]
                    title: 'Nedsæt forsikringssum',
                    description:
                        'Før du kan nedsætte din forsikringssum, har vi brug for at tale med dig og rådgive dig omkring konsekvenserne.'
                }
            },
            hidden: isOpc && !isInsuranceSumAvailableForUpdate
        },
        {
            key: AdjustItemKeys.ACC_ADULT_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            name: 'Méngrad',
            fieldValue: `${getTermValue(
                adult,
                PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
                ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM
            )}%`,
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                changeType: 'tilpas',
                productName: 'top_acc_accident',
                coverableId,
                coverageId: PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
                adjustAction: 'InjuryDegree',
                termId: ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM
            },
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-TITLE',
                    description:
                        getTermValue(
                            adult,
                            'ACCPermanentInjuryToothDamageCov',
                            'ACCPermanentInjuryToothDamageCovCovPIDegree'
                        ) === '5'
                            ? 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-FIVE-PERCENT'
                            : 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-EIGHT-PERCENT'
                }
            },
            hidden: isOpc && !isInjuryDegreeAvailableForUpdate
        },
        {
            key: AdjustItemKeys.ACC_ADULT_CONTRACT_TYPE,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            name: 'Variant',
            fieldValue: mapContractTypeToLabel(adult.contractType ?? ''),
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                changeType: 'attribut',
                productName: 'top_acc_accident',
                coverableId,
                attribute: 'adultAccContractType'
            },
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-CONTRACTTYPE-TITLE',
                    description: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-CONTRACTTYPE'
                }
            },
            hidden: isOpc && !isContractTypeAvailableForUpdate
        },
        {
            key: AdjustItemKeys.ACC_ADULT_FULLTIME_LEISURE,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            name: 'Tidsrum',
            fieldValue: adult.fullTimeOrLeisure,
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                changeType: 'attribut',
                productName: 'top_acc_accident',
                coverableId,
                attribute: 'adultAccFulltimeLeisure'
            },
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: 'Tidsrum',
                    description: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-FULLTIMEORLEISURE'
                }
            },
            hidden: isOpc && !isFullTimeOrLeisureAvailableForUpdate
        },
        ...beneficiary
    ];
};

const getYoungAccidentAdjustDetailsConfig = (
    young: YoungAccidentDTO,
    changes: DiffDTO[],
    isOpc: boolean
): OPCRowInformation[] => {
    const isInsuranceSumAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'term',
            coverageId: PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
            termId: ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
        },
        changes
    );
    const isInjuryDegreeAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'term',
            coverageId: PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
            termId: ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM
        },
        changes
    );
    const isFullTimeOrLeisureAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'attribute',
            attributeFieldName: 'FullTimeOrLeisure'
        },
        changes
    );

    return [
        {
            key: AdjustItemKeys.ACC_YOUNG_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            name: 'Forsikringssum',
            fieldValue: getTermMonetaryValue(
                young,
                PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
                ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
            ),
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                // Todo: Use hippo label when label id is define in document [Hippo ACC]
                changeType: 'tilpas',
                productName: 'top_acc_young',
                coverableId: young.fixedId ?? '',
                coverageId: PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
                adjustAction: 'Limit',
                termId: ACC_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
            },
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    // Todo: Use hippo label when label id is define in document [Hippo ACC]
                    title: 'Nedsæt forsikringssum',
                    description:
                        'Før du kan nedsætte din forsikringssum, har vi brug for at tale med dig og rådgive dig omkring konsekvenserne.'
                }
            },
            hidden: isOpc && !isInsuranceSumAvailableForUpdate
        },
        {
            key: AdjustItemKeys.ACC_YOUNG_INJURY_TOOTH_DAMAGE_DEGREE_TERM,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            name: 'Méngrad',
            fieldValue: `${getTermValue(
                young,
                PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
                ACC_INJURY_TOOTH_DAMAGE_DEGREE_TERM
            )}%`,
            isAdjustableItem: false,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-TITLE',
                    description: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-MENGRAD-EIGHT-PERCENT'
                }
            },
            hidden: isOpc && !isInjuryDegreeAvailableForUpdate
        },
        {
            key: AdjustItemKeys.ACC_YOUNG_FULLTIME_LEISURE,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            name: 'Tidsrum',
            fieldValue: young.fullTimeOrLeisure,
            isAdjustableItem: false,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: 'Tidsrum',
                    description: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-FULLTIMEORLEISURE'
                }
            },
            hidden: isOpc && !isFullTimeOrLeisureAvailableForUpdate
        }
    ];
};

const getChildAccidentAdjustDetailsConfig = (
    child: ChildAccidentDTO,
    changes: DiffDTO[],
    isOpc: boolean
): OPCRowInformation[] => {
    const isInsuranceSumAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'term',
            coverageId: CHILD_PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
            termId: CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
        },
        changes
    );
    const isFullTimeOrLeisureAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'attribute',
            attributeFieldName: 'FullTimeOrLeisure'
        },
        changes
    );

    return [
        {
            key: AdjustItemKeys.ACC_CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            name: 'Forsikringssum',
            fieldValue: getTermMonetaryValue(
                child,
                CHILD_PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
                CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
            ),
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                // Todo: Use hippo label when label id is define in document [Hippo ACC]
                changeType: 'tilpas',
                productName: 'top_acc_child',
                coverableId: child.fixedId ?? '',
                coverageId: CHILD_PERMANENT_INJURY_AND_TOOTH_DAMAGE_COVERAGE,
                adjustAction: 'Limit',
                termId: CHILD_INJURY_TOOTH_DAMAGE_SUM_INSURED_TERM
            },
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    // Todo: Use hippo label when label id is define in document [Hippo ACC]
                    title: 'Nedsæt forsikringssum',
                    description:
                        'Før du kan nedsætte din forsikringssum, har vi brug for at tale med dig og rådgive dig omkring konsekvenserne.'
                }
            },
            hidden: isOpc && !isInsuranceSumAvailableForUpdate
        },
        {
            key: AdjustItemKeys.ACC_CHILD_FULLTIME_LEISURE,
            // Todo: Use hippo label when label id is define in document [Hippo ACC]
            name: 'Tidsrum',
            fieldValue: child.fullTimeOrLeisure,
            isAdjustableItem: false,
            labelType: 'modalLink',
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: 'Tidsrum',
                    description: 'DSS-ACC-HIGHLIGHTED-TEXT-MODAL-FULLTIMEORLEISURE'
                }
            },
            hidden: isOpc && !isFullTimeOrLeisureAvailableForUpdate
        }
    ];
};

const getAccidentAdjustDetailsConfig = (
    accident: CoverableDTO,
    isOpc: boolean,
    coverableChangesDTO: CoverableChangesDTO[]
): OPCRowInformation[] => {
    const changes = getCoverableDiffsDTO(
        coverableChangesDTO,
        LINE_PREFIX_ACC,
        accident.fixedId ?? ''
    );

    switch (accident.flavourName) {
        case 'Ulykkesforsikring':
            return getAdultAccidentAdjustDetailsConfig(accident, changes, isOpc);
        case 'Ulykkesforsikring til unge':
            return getYoungAccidentAdjustDetailsConfig(accident, changes, isOpc);
        case 'Børneforsikring':
            return getChildAccidentAdjustDetailsConfig(accident, changes, isOpc);

        default:
            throw new Error('Coverable is not accident flavour');
    }
};

export default getAccidentAdjustDetailsConfig;
